import { ComponentRef, inject, Injectable, Injector, Type, ViewContainerRef } from '@angular/core';
import { ContentModel, ContentType } from '@libs/cms/models';
import { SpinnerComponent } from '@libs/common/components';
import { firstValueFrom } from 'rxjs';
import {
  AccordionContentComponent,
  AnchorComponent,
  AdvertisingModalContentComponent,
  ContentSliderComponent,
  ContentTeasersComponent,
  CtaBarComponent,
  CtaListComponent,
  DigitalServiceComponent,
  DownloadListComponent,
  FormContentComponent,
  HeadlineComponent,
  HeroVideoComponent,
  InfoBarContentComponent,
  KeyFigureCardSliderComponent,
  MediaContentComponent,
  PlainComponent,
  ProductContentComponent,
  ProductContentSkeletonComponent,
  RichtextComponent,
  ServiceTeasersComponent,
  TextMediaComponent,
  TopicCardsComponent,
  VideoContentComponent,
} from '../../content';
import { ContentRemoteDataResolverService } from './content-remote-data-resolver.service';

export type ContentComponent =
  | AccordionContentComponent
  | AnchorComponent
  | ContentTeasersComponent
  | CtaBarComponent
  | CtaListComponent
  | DigitalServiceComponent
  | DownloadListComponent
  | FormContentComponent
  | HeadlineComponent
  | HeroVideoComponent
  | InfoBarContentComponent
  | KeyFigureCardSliderComponent
  | MediaContentComponent
  | AdvertisingModalContentComponent
  | PlainComponent
  | ProductContentComponent
  | RichtextComponent
  | ServiceTeasersComponent
  | ContentSliderComponent
  | TextMediaComponent
  | TopicCardsComponent
  | VideoContentComponent;

export type ContentLoaderComponent = ProductContentSkeletonComponent | SpinnerComponent;

export type ContentComponentType = Type<ContentComponent>;
export type ContentLoaderComponentType = Type<ContentLoaderComponent>;

@Injectable()
export class ContentBuilderService {
  private readonly contentResolverService: ContentRemoteDataResolverService = inject(ContentRemoteDataResolverService);

  private readonly contentComponentMap: Record<ContentType, ContentComponentType> = {
    accordion: AccordionContentComponent,
    anchor: AnchorComponent,
    content_teasers: ContentTeasersComponent,
    cta_bar: CtaBarComponent,
    cta_list: CtaListComponent,
    digital_service: DigitalServiceComponent,
    download_list: DownloadListComponent,
    form: FormContentComponent,
    headline: HeadlineComponent,
    hero_video: HeroVideoComponent,
    info_bar: InfoBarContentComponent,
    key_figure_card_slider: KeyFigureCardSliderComponent,
    media: MediaContentComponent,
    modal: AdvertisingModalContentComponent,
    plain: PlainComponent,
    product_content: ProductContentComponent,
    richtext: RichtextComponent,
    service_teasers: ServiceTeasersComponent,
    slider: ContentSliderComponent,
    text_media: TextMediaComponent,
    topic_cards: TopicCardsComponent,
    video: VideoContentComponent,
  };

  private readonly contentComponentLoaderMap: Partial<Record<ContentType, ContentLoaderComponentType>> = {
    product_content: ProductContentSkeletonComponent,
  };

  public build(content: ContentModel[], viewContainerRef: ViewContainerRef, injector?: Injector): void {
    content.forEach((item: ContentModel, index: number) => {
      this.injectComponentIntoViewContainer(item, viewContainerRef, index, injector);
    });
  }

  private injectComponentIntoViewContainer(content: ContentModel, viewContainerRef: ViewContainerRef, index: number, injector?: Injector): void {
    const componentConstructor: ContentComponentType | undefined = this.contentComponentMap[content.type];

    if (!componentConstructor) {
      this.handleComponentUndefined(content);

      return;
    }

    if (!content.remoteDataConfig) {
      this.createComponent(componentConstructor, content, viewContainerRef, index, injector);

      return;
    }

    void this.createComponentWithRemoteData(componentConstructor, content, viewContainerRef, index, injector);
  }

  private createComponentWithRemoteData(
    componentConstructor: ContentComponentType,
    content: ContentModel,
    viewContainerRef: ViewContainerRef,
    index: number,
    injector?: Injector,
  ): Promise<void> {
    const loader: Type<ContentLoaderComponent> = this.contentComponentLoaderMap[content.type] ?? SpinnerComponent;
    const loaderRef: ComponentRef<ContentLoaderComponent> = viewContainerRef.createComponent(loader, { index });

    if (loader !== SpinnerComponent) {
      loaderRef.setInput('data', content);
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return firstValueFrom(this.contentResolverService.resolve(content.remoteDataConfig!)).then((response) => {
      if (!response || (Array.isArray(response) && response.length === 0)) {
        return;
      }

      content.remoteData = response;
      this.createComponent(componentConstructor, content, viewContainerRef, index, injector);
    }).finally(() => {
      loaderRef.destroy();
    });
  }

  private createComponent(componentConstructor: ContentComponentType, content: ContentModel, viewContainerRef: ViewContainerRef, index: number, injector?: Injector): void {
    const componentOptions: { index: number; injector: Injector } | { index: number } = injector
      ? { index, injector }
      : { index };
    const component: ComponentRef<ContentComponent> = viewContainerRef.createComponent(componentConstructor, componentOptions);

    component.setInput('data', content);
  }

  private handleComponentUndefined(content: ContentModel): void {
    console.warn(
      `Content item for model '${content.type}' is not defined!`,
    );
  }
}
